import { ArrowLeft01Icon, ArrowRight01Icon, Calendar03Icon, Globe02Icon } from '@hugeicons/core-free-icons';
import { Icon } from './icon.js';
import { Popover as PopoverPrimitive } from 'radix-ui';
import { DayPicker, type DayPickerProps } from 'react-day-picker';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { formatTimezone } from '../../lib/format.js';
import { controlBase } from './input.js';
import { Select, type SelectProps } from './select.js';

// Dates here are calendar days (YYYY-MM-DD) interpreted in the business timezone. Helpers convert between
// those strings and browser-local midnight Date objects so no timezone shift occurs in the calendar grid.

export function parseLocalDay(day: string): Date {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

export function toLocalDay(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

const dayLabelFmt = new Intl.DateTimeFormat('es-MX', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
const captionFmt = new Intl.DateTimeFormat('es-MX', { month: 'long', year: 'numeric' });
const weekdayFmt = new Intl.DateTimeFormat('es-MX', { weekday: 'short' });
const weekdayLongFmt = new Intl.DateTimeFormat('es-MX', { weekday: 'long' });
const fullDayFmt = new Intl.DateTimeFormat('es-MX', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

/** "2026-09-28" → "28 sept 2026" (calendar day, no timezone shift). */
export function formatLocalDay(day: string): string {
  return dayLabelFmt.format(new Date(`${day}T12:00:00Z`));
}

const navButton = cn(
  'inline-flex size-8 items-center justify-center rounded-control border border-border-subtle text-text-secondary',
  'hover:bg-surface-subtle focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-focus disabled:opacity-40',
);

export type CalendarProps = DayPickerProps;

/** react-day-picker themed with Estateflow tokens, Spanish labels and Monday as first day. */
export function Calendar({ className, classNames, showOutsideDays = true, ...props }: CalendarProps) {
  return (
    <DayPicker
      weekStartsOn={1}
      showOutsideDays={showOutsideDays}
      className={cn('p-3 text-body', className)}
      formatters={{
        formatCaption: (month: Date) => captionFmt.format(month),
        formatWeekdayName: (d: Date) => weekdayFmt.format(d).replace('.', ''),
      }}
      labels={{
        labelNext: () => 'Mes siguiente',
        labelPrevious: () => 'Mes anterior',
        labelNav: () => 'Navegación del calendario',
        labelGrid: (d: Date) => captionFmt.format(d),
        labelWeekday: (d: Date) => weekdayLongFmt.format(d),
        labelDayButton: (d: Date, modifiers: Record<string, boolean>) =>
          `${modifiers['today'] ? 'Hoy, ' : ''}${fullDayFmt.format(d)}${modifiers['selected'] ? ', seleccionado' : ''}`,
      }}
      classNames={{
        months: 'relative flex flex-col gap-4 sm:flex-row',
        month: 'flex flex-col gap-3',
        month_caption: 'flex h-8 items-center justify-center',
        caption_label: 'text-body font-semibold first-letter:uppercase text-text-primary',
        nav: 'absolute inset-x-0 top-0 flex h-8 items-center justify-between',
        button_previous: navButton,
        button_next: navButton,
        month_grid: 'w-full border-collapse',
        weekdays: 'flex',
        weekday: 'w-9 text-meta font-medium text-text-secondary',
        week: 'mt-1 flex w-full',
        day: 'relative size-9 p-0 text-center',
        day_button: cn(
          'ef-tabular inline-flex size-9 items-center justify-center rounded-control text-body text-text-primary',
          'transition-colors duration-[var(--ef-motion-fast)] hover:bg-surface-subtle',
          'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-focus',
        ),
        selected: '[&>button]:bg-brand-primary [&>button]:font-semibold [&>button]:text-text-on-primary [&>button]:hover:bg-brand-hover',
        today: '[&>button]:font-semibold [&>button]:underline [&>button]:underline-offset-4',
        outside: '[&>button]:text-text-secondary [&>button]:opacity-70',
        disabled: '[&>button]:cursor-not-allowed [&>button]:opacity-40',
        hidden: 'invisible',
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation, className: c }) =>
          orientation === 'left' ? <Icon icon={ArrowLeft01Icon} aria-hidden className={cn('size-4', c)} /> : <Icon icon={ArrowRight01Icon} aria-hidden className={cn('size-4', c)} />,
      }}
      {...props}
    />
  );
}

export interface DatePickerProps {
  /** Local calendar day YYYY-MM-DD, or '' when empty. */
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  /** Inclusive bounds (YYYY-MM-DD). */
  min?: string;
  max?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
  'aria-label'?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
  'aria-required'?: boolean;
}

/** Control-styled trigger that opens a Calendar in a popover. */
export const DatePicker = React.forwardRef<HTMLButtonElement, DatePickerProps>(function DatePicker(
  { value, onValueChange, placeholder = 'Seleccionar fecha', min, max, disabled, id, className, ...aria },
  ref,
) {
  const [open, setOpen] = React.useState(false);
  const selected = value ? parseLocalDay(value) : undefined;
  const bounds = [...(min ? [{ before: parseLocalDay(min) }] : []), ...(max ? [{ after: parseLocalDay(max) }] : [])];
  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger
        ref={ref}
        id={id}
        disabled={disabled}
        aria-haspopup="dialog"
        className={cn(controlBase, 'flex items-center gap-2 text-left', !value && 'text-text-secondary', className)}
        {...aria}
      >
        <Icon icon={Calendar03Icon} aria-hidden className="size-[18px] shrink-0 text-text-secondary" />
        <span className="ef-tabular truncate">{value ? formatLocalDay(value) : placeholder}</span>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={6}
          aria-label="Calendario"
          className="z-50 rounded-menu border border-border-subtle bg-surface-default shadow-overlay animate-fade-in"
        >
          <Calendar
            mode="single"
            selected={selected}
            defaultMonth={selected}
            disabled={bounds}
            autoFocus
            onSelect={(d) => {
              if (!d) return;
              onValueChange(toLocalDay(d));
              setOpen(false);
            }}
          />
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
});

function timeOptions(stepMinutes: number): { value: string; label: string }[] {
  const out: { value: string; label: string }[] = [];
  for (let m = 0; m < 24 * 60; m += stepMinutes) {
    const v = `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
    out.push({ value: v, label: v });
  }
  return out;
}

export interface TimeSelectProps extends Omit<SelectProps, 'options'> {
  /** Minutes between options (default 30). The current value is always offered even if off-step. */
  stepMinutes?: number;
  /** Adds "24:00" (end of day) as the last option. */
  allowEndOfDay?: boolean;
}

/** Time-of-day select (HH:MM, 24 h). */
export const TimeSelect = React.forwardRef<HTMLButtonElement, TimeSelectProps>(function TimeSelect(
  { stepMinutes = 30, allowEndOfDay, value, className, placeholder = 'HH:MM', ...props },
  ref,
) {
  const options = React.useMemo(() => {
    const base = timeOptions(stepMinutes);
    if (allowEndOfDay) base.push({ value: '24:00', label: '24:00' });
    if (value && !base.some((o) => o.value === value)) {
      base.push({ value, label: value });
      base.sort((a, b) => a.value.localeCompare(b.value));
    }
    return base;
  }, [stepMinutes, value, allowEndOfDay]);
  return <Select ref={ref} value={value || undefined} options={options} placeholder={placeholder} className={cn('ef-tabular', className)} {...props} />;
});

export interface DateTimeValue {
  /** YYYY-MM-DD or '' */
  date: string;
  /** HH:MM or '' */
  time: string;
}

export interface DateTimeFieldProps {
  value: DateTimeValue;
  onValueChange: (value: DateTimeValue) => void;
  /** IANA timezone the wall-clock value is interpreted in (shown to the user). */
  timezone: string;
  minDate?: string;
  maxDate?: string;
  stepMinutes?: number;
  disabled?: boolean;
  /** Applied to the date trigger so a FormField label targets it. */
  id?: string;
  className?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
  'aria-required'?: boolean;
}

/** Date + time with an explicit timezone label. Wrap in FormField for label/error wiring. */
export const DateTimeField = React.forwardRef<HTMLButtonElement, DateTimeFieldProps>(function DateTimeField(
  { value, onValueChange, timezone, minDate, maxDate, stepMinutes = 15, disabled, id, className, ...aria },
  ref,
) {
  const tzId = React.useId();
  const describedBy = [aria['aria-describedby'], tzId].filter(Boolean).join(' ');
  const tzAt = value.date ? `${value.date}T12:00:00Z` : undefined;
  return (
    <div className={cn('grid gap-1.5', className)}>
      <div className="grid grid-cols-[minmax(0,1fr)_7.5rem] gap-2">
        <DatePicker
          ref={ref}
          id={id}
          value={value.date}
          onValueChange={(date) => onValueChange({ ...value, date })}
          min={minDate}
          max={maxDate}
          disabled={disabled}
          aria-invalid={aria['aria-invalid']}
          aria-required={aria['aria-required']}
          aria-describedby={describedBy}
        />
        <TimeSelect
          aria-label="Hora"
          value={value.time}
          onValueChange={(time) => onValueChange({ ...value, time })}
          stepMinutes={stepMinutes}
          disabled={disabled}
          aria-invalid={aria['aria-invalid']}
          aria-describedby={describedBy}
        />
      </div>
      <p id={tzId} className="flex items-center gap-1.5 text-meta text-text-secondary">
        <Icon icon={Globe02Icon} aria-hidden className="size-3.5" />
        Zona horaria: {formatTimezone(timezone, tzAt)}
      </p>
    </div>
  );
});
